import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PlacedPart } from '../domain/assembly'
import {
  allCodingBlocks,
  codingBlockDefinition,
  codingCommandCount,
  codingProgramEstimatedSeconds,
  type CodingProgramBlock,
} from '../domain/coding'
import { codingMotorCubeNumbers, runCodingProgram } from '../domain/coding-runtime'
import { orderedCubes, type MotorControl } from '../domain/motor-control'
import { connectedAssemblyForPart } from '../domain/simulation-analysis'
import { useStudioStore } from '../store/useStudioStore'
import { Icon } from './Icon'
import { SceneErrorBoundary } from './SceneErrorBoundary'

const BabylonWorkspace = lazy(() =>
  import('./BabylonWorkspace').then((module) => ({ default: module.BabylonWorkspace })),
)

type CameraActionType = 'reset' | 'focus' | 'all' | 'top' | 'front' | 'context'
type CodingRunStatus = 'idle' | 'manual' | 'running' | 'complete'

type CodingSimulationSnapshot = {
  blocks: CodingProgramBlock[]
  cubeInstanceIds: string[]
}

const KEYBOARD_MOVE_STEP = 0.25
const KEYBOARD_ROTATION_STEP = Math.PI / 12

function rotatedAssemblyTransforms(
  parts: PlacedPart[],
  axis: 'x' | 'y' | 'z',
  angle: number,
) {
  if (!parts.length) return {}
  const center = parts.reduce((sum, part) => ({
    x: sum.x + part.position.x / parts.length,
    y: sum.y + part.position.y / parts.length,
    z: sum.z + part.position.z / parts.length,
  }), { x: 0, y: 0, z: 0 })
  const cosine = Math.cos(angle)
  const sine = Math.sin(angle)

  return Object.fromEntries(parts.map((part) => {
    const x = part.position.x - center.x
    const y = part.position.y - center.y
    const z = part.position.z - center.z
    const position = axis === 'x'
      ? { x, y: y * cosine - z * sine, z: y * sine + z * cosine }
      : axis === 'y'
        ? { x: x * cosine + z * sine, y, z: -x * sine + z * cosine }
        : { x: x * cosine - y * sine, y: x * sine + y * cosine, z }
    return [part.instanceId, {
      position: {
        x: Number((center.x + position.x).toFixed(6)),
        y: Number((center.y + position.y).toFixed(6)),
        z: Number((center.z + position.z).toFixed(6)),
      },
      rotation: {
        ...part.rotation,
        [axis]: part.rotation[axis] + angle,
      },
    }]
  }))
}

function Toggle({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return <button aria-label={label} aria-pressed={active} className={`toggle ${active ? 'on' : ''}`} onClick={onClick}><span /></button>
}

export function Workspace() {
  const [transformMode, setTransformMode] = useState<'move' | 'rotate'>('move')
  const [cameraToolsOpen, setCameraToolsOpen] = useState(false)
  const [assemblyToolsOpen, setAssemblyToolsOpen] = useState(false)
  const [codingRunStatus, setCodingRunStatus] = useState<CodingRunStatus>('idle')
  const [activeCodingBlockId, setActiveCodingBlockId] = useState<string | null>(null)
  const codingRunSnapshotRef = useRef<CodingSimulationSnapshot | null>(null)
  const codingAbortRef = useRef<AbortController | null>(null)
  const [cameraAction, setCameraAction] = useState<{ type: CameraActionType; nonce: number }>({
    type: 'all',
    nonce: 0,
  })
  const {
    mode, autoSnap, gravity, preventOverlap, solidFloor, environment, placedParts, connections, robotGroups, motorControls, runtimeMotorControls, codingProgramName, codingProgram, simulationObstacles, partLimit, selectedInstanceId, selectedInstanceIds, isSimulating,
    setMode, toggleAutoSnap, toggleGravity, togglePreventOverlap, toggleSolidFloor, toggleSimulation, setRuntimeMotorControls, selectInstance, rotateSelectedAxis,
    duplicateSelectedPart, removeSelectedPart, moveSelectedPart, moveSelectedVertical,
    beginSelectedTransform, setSelectedTransform, addPartAt, selectInstances, selectAllInstances, groupSelected, ungroupSelected, moveConnectedGroup, rotateConnectedGroup, moveSimulationObstacle, applySimulationImpact,
  } = useStudioStore()
  const activeRobotGroup = robotGroups.find((group) =>
    group.instanceIds.includes(selectedInstanceId ?? '')
    && group.instanceIds.length === selectedInstanceIds.length
    && group.instanceIds.every((id) => selectedInstanceIds.includes(id))) ?? null
  const simulationSelectedInstanceIds = mode === 'simulation'
    ? connectedAssemblyForPart(placedParts, connections, selectedInstanceId)
      .map((part) => part.instanceId)
    : selectedInstanceIds
  const simulationTargetAssembly = useMemo(() => (
    mode === 'simulation' && selectedInstanceId
      ? connectedAssemblyForPart(placedParts, connections, selectedInstanceId)
      : []
  ), [connections, mode, placedParts, selectedInstanceId])
  const simulationTargetCubes = orderedCubes(simulationTargetAssembly)
  const codingCommands = codingCommandCount(codingProgram)
  const hasCodingProgram = codingCommands > 0
  const referencedCubeNumbers = codingMotorCubeNumbers(codingProgram)
  const missingCubeNumber = referencedCubeNumbers.find((number) => number > simulationTargetCubes.length) ?? null
  const codingBlockedReason = hasCodingProgram
    ? !selectedInstanceId
      ? '실행할 로봇을 먼저 선택하세요'
      : missingCubeNumber
        ? `${missingCubeNumber}번 큐브가 선택한 로봇에 없습니다`
        : null
    : null
  const effectiveMotorControls = runtimeMotorControls ?? motorControls
  const activeCodingProgramBlock = activeCodingBlockId
    ? allCodingBlocks(codingProgram).find((block) => block.id === activeCodingBlockId)
    : null
  const activeCodingBlock = activeCodingProgramBlock
    ? codingBlockDefinition(activeCodingProgramBlock.type)
    : null
  const runCameraAction = useCallback((type: CameraActionType) => {
    setCameraAction((state) => ({ type, nonce: state.nonce + 1 }))
  }, [])
  const previousCameraContextRef = useRef({ mode, environment })

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target
      if (target instanceof Element && target.matches('input, textarea, [contenteditable="true"]')) return
      if (mode === 'simulation') {
        if (event.code === 'Digit1') setTransformMode('move')
        if (event.code === 'Digit2' && !isSimulating) setTransformMode('rotate')
        const axisKey = ['KeyW', 'KeyS', 'KeyA', 'KeyD', 'KeyQ', 'KeyE'].includes(event.code)
        if (axisKey && !isSimulating && simulationTargetAssembly.length) {
          event.preventDefault()
          const ids = simulationTargetAssembly.map((part) => part.instanceId)
          if (transformMode === 'move') {
            if (event.code === 'KeyW') moveConnectedGroup(ids, 0, -KEYBOARD_MOVE_STEP)
            if (event.code === 'KeyS') moveConnectedGroup(ids, 0, KEYBOARD_MOVE_STEP)
            if (event.code === 'KeyA') moveConnectedGroup(ids, -KEYBOARD_MOVE_STEP, 0)
            if (event.code === 'KeyD') moveConnectedGroup(ids, KEYBOARD_MOVE_STEP, 0)
            if (event.code === 'KeyQ') moveConnectedGroup(ids, 0, 0, KEYBOARD_MOVE_STEP)
            if (event.code === 'KeyE') moveConnectedGroup(ids, 0, 0, -KEYBOARD_MOVE_STEP)
          } else {
            const rotation = event.code === 'KeyW' ? ['x', 1]
              : event.code === 'KeyS' ? ['x', -1]
                : event.code === 'KeyA' ? ['y', -1]
                  : event.code === 'KeyD' ? ['y', 1]
                    : event.code === 'KeyQ' ? ['z', 1]
                      : ['z', -1]
            rotateConnectedGroup(rotatedAssemblyTransforms(
              simulationTargetAssembly,
              rotation[0] as 'x' | 'y' | 'z',
              KEYBOARD_ROTATION_STEP * (rotation[1] as number),
            ))
          }
        }
        if (event.code === 'KeyF') runCameraAction('focus')
        if (event.code === 'Digit0') runCameraAction('reset')
        if (event.key === 'Escape') selectInstance(null)
        return
      }
      if (mode !== 'assembly') return
      if ((event.metaKey || event.ctrlKey) && event.code === 'KeyA') {
        event.preventDefault()
        selectAllInstances()
        return
      }
      if ((event.metaKey || event.ctrlKey) && event.code === 'KeyD') {
        event.preventDefault()
        duplicateSelectedPart()
        return
      }
      if ((event.metaKey || event.ctrlKey) && event.code === 'KeyG') {
        event.preventDefault()
        if (event.shiftKey) ungroupSelected()
        else groupSelected()
        return
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault()
        removeSelectedPart()
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        moveSelectedPart(-1, 0)
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        moveSelectedPart(1, 0)
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        moveSelectedPart(0, -1)
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        moveSelectedPart(0, 1)
      }
      if (event.code === 'Digit1') setTransformMode('move')
      if (event.code === 'Digit2') setTransformMode('rotate')
      const axisKey = ['KeyW', 'KeyS', 'KeyA', 'KeyD', 'KeyQ', 'KeyE'].includes(event.code)
      if (axisKey) {
        event.preventDefault()
        if (transformMode === 'move') {
          if (event.code === 'KeyW') moveSelectedPart(0, -1)
          if (event.code === 'KeyS') moveSelectedPart(0, 1)
          if (event.code === 'KeyA') moveSelectedPart(-1, 0)
          if (event.code === 'KeyD') moveSelectedPart(1, 0)
          if (event.code === 'KeyQ') moveSelectedVertical(1)
          if (event.code === 'KeyE') moveSelectedVertical(-1)
        } else {
          if (event.code === 'KeyW') rotateSelectedAxis('x', 1)
          if (event.code === 'KeyS') rotateSelectedAxis('x', -1)
          if (event.code === 'KeyA') rotateSelectedAxis('y', -1)
          if (event.code === 'KeyD') rotateSelectedAxis('y', 1)
          if (event.code === 'KeyQ') rotateSelectedAxis('z', 1)
          if (event.code === 'KeyE') rotateSelectedAxis('z', -1)
        }
      }
      if (event.code === 'KeyF') runCameraAction('focus')
      if (event.code === 'Digit0') runCameraAction('reset')
      if (event.key === 'Escape') selectInstance(null)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [duplicateSelectedPart, groupSelected, isSimulating, mode, moveConnectedGroup, moveSelectedPart, moveSelectedVertical, removeSelectedPart, rotateConnectedGroup, rotateSelectedAxis, runCameraAction, selectAllInstances, selectInstance, simulationTargetAssembly, transformMode, ungroupSelected])

  useEffect(() => {
    const previous = previousCameraContextRef.current
    const contextChanged = previous.mode !== mode || previous.environment !== environment
    previousCameraContextRef.current = { mode, environment }
    if (!contextChanged) return
    runCameraAction('context')
    setCameraToolsOpen(false)
  }, [environment, isSimulating, mode, runCameraAction])

  useEffect(() => {
    if (mode === 'simulation' && isSimulating) setTransformMode('move')
  }, [isSimulating, mode])

  useEffect(() => {
    if (!isSimulating) return
    const snapshot = codingRunSnapshotRef.current
    if (!snapshot) {
      setCodingRunStatus('manual')
      return
    }

    const controller = new AbortController()
    codingAbortRef.current = controller
    const stoppedControls = Object.fromEntries(snapshot.cubeInstanceIds.map((cubeInstanceId) => [
      cubeInstanceId,
      { direction: 'stop', speedPercent: 0 } satisfies MotorControl,
    ]))
    setRuntimeMotorControls(stoppedControls)
    setCodingRunStatus('running')

    void runCodingProgram(
      snapshot.blocks,
      snapshot.cubeInstanceIds,
      {
        setMotor: (cubeInstanceId, control) => setRuntimeMotorControls({
          ...(useStudioStore.getState().runtimeMotorControls ?? {}),
          [cubeInstanceId]: control,
        }),
        stopAll: (cubeInstanceIds) => setRuntimeMotorControls(Object.fromEntries(
          cubeInstanceIds.map((cubeInstanceId) => [
            cubeInstanceId,
            { direction: 'stop', speedPercent: 0 } satisfies MotorControl,
          ]),
        )),
        onBlock: (block) => setActiveCodingBlockId(block.id),
      },
      { signal: controller.signal },
    ).then((result) => {
      if (result !== 'completed' || controller.signal.aborted) return
      setActiveCodingBlockId(null)
      setCodingRunStatus('complete')
      useStudioStore.setState({
        notice: `${codingProgramName || '메인 프로그램'} 실행을 완료했습니다. 시뮬레이션은 계속됩니다.`,
      })
    })

    return () => controller.abort()
  }, [codingProgramName, isSimulating, setRuntimeMotorControls])

  useEffect(() => () => {
    codingAbortRef.current?.abort()
    useStudioStore.getState().setRuntimeMotorControls(null)
  }, [])

  const toggleSimulationFromProgramBar = () => {
    if (isSimulating) {
      codingAbortRef.current?.abort()
      codingRunSnapshotRef.current = null
      setRuntimeMotorControls(null)
      setActiveCodingBlockId(null)
      setCodingRunStatus('idle')
      toggleSimulation()
      return
    }

    if (hasCodingProgram && !codingBlockedReason) {
      codingRunSnapshotRef.current = {
        blocks: codingProgram,
        cubeInstanceIds: simulationTargetCubes.map((cube) => cube.instanceId),
      }
      setCodingRunStatus('running')
    } else {
      codingRunSnapshotRef.current = null
      setCodingRunStatus('manual')
    }
    toggleSimulation()
  }

  return (
    <section
      className="workspace"
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes('application/x-pingpong-part')) event.preventDefault()
      }}
      onDrop={(event) => {
        const partId = event.dataTransfer.getData('application/x-pingpong-part')
        if (!partId) return
        event.preventDefault()
        const bounds = event.currentTarget.getBoundingClientRect()
        const normalizedX = (event.clientX - bounds.left) / bounds.width - 0.5
        const normalizedZ = (event.clientY - bounds.top) / bounds.height - 0.5
        const snap = (value: number) => autoSnap ? Math.round(value * 4) / 4 : Number(value.toFixed(2))
        addPartAt(partId, {
          x: snap(normalizedX * 10),
          y: 0.65,
          z: snap(normalizedZ * 7),
        })
      }}
    >
      {mode === 'assembly' && (
        <details
          className="workspace-tools"
          aria-label="조립 보조 설정"
          open={assemblyToolsOpen}
          onToggle={(event) => setAssemblyToolsOpen(event.currentTarget.open)}
        >
          <summary><strong>조립 보조</strong><small>{assemblyToolsOpen ? '접기' : '펼치기'}</small></summary>
          <div className="workspace-tool-options">
            <span title="부품의 위치를 0.25 간격 좌표에 맞춰 반듯하게 놓습니다.">
              <i><b>격자 스냅</b><small>일정 간격으로 맞춤</small></i>
              <Toggle label="격자 스냅" active={autoSnap} onClick={toggleAutoSnap} />
            </span>
            <span>
              <i><b>겹침 방지</b><small>부품 관통 차단</small></i>
              <Toggle label="겹침 방지" active={preventOverlap} onClick={togglePreventOverlap} />
            </span>
            <span>
              <i><b>바닥 표시</b><small>도로 재질 표시</small></i>
              <Toggle label="바닥 표시" active={solidFloor} onClick={toggleSolidFloor} />
            </span>
            <span>
              <i><b>물리 중력</b><small>접지 계산 사용</small></i>
              <Toggle label="물리 중력" active={gravity} onClick={toggleGravity} />
            </span>
          </div>
        </details>
      )}
      {mode === 'assembly' && partLimit !== null && <div className="workspace-budget" aria-label="부품 제한">
        <div><span>미션 부품 제한</span><strong>{placedParts.length} / {partLimit}</strong></div>
        <div className="budget-bar"><span style={{ width: `${Math.min(placedParts.length / partLimit * 100, 100)}%` }} /></div>
      </div>}

      <div className={`transform-toolbar ${mode === 'simulation' ? 'simulation-transform-toolbar' : ''}`} aria-label="이동·회전 모드">
        <button
          className={transformMode === 'move' ? 'active' : ''}
          disabled={mode === 'simulation' && isSimulating}
          onClick={() => setTransformMode('move')}
        >
          <b>1 · {mode === 'simulation' ? '로봇 이동' : '이동'}</b>
        </button>
        <button
          className={transformMode === 'rotate' ? 'active rotate' : ''}
          disabled={mode === 'simulation' && isSimulating}
          onClick={() => setTransformMode('rotate')}
        >
          <b>2 · {mode === 'simulation' ? '로봇 회전' : '회전'}</b>
        </button>
      </div>

      {mode === 'assembly' && (
        <div className="robot-group-toolbar" aria-label="로봇 그룹 작업">
          <span>
            <b>{activeRobotGroup
              ? activeRobotGroup.name
              : selectedInstanceIds.length
                ? `${selectedInstanceIds.length}개 선택`
                : '영역 선택'}</b>
            <small>{activeRobotGroup
              ? `연결 부품 ${activeRobotGroup.instanceIds.length}개`
              : 'Shift + 왼쪽 드래그'}</small>
          </span>
          {activeRobotGroup ? (
            <button type="button" onClick={ungroupSelected}>그룹 해제</button>
          ) : (
            <button type="button" disabled={selectedInstanceIds.length < 2} onClick={groupSelected}>
              로봇으로 그룹화
            </button>
          )}
        </div>
      )}

      <SceneErrorBoundary>
        <Suspense fallback={<div className="scene-loading">3D 작업실을 준비하고 있습니다…</div>}>
          <BabylonWorkspace
            parts={placedParts}
            connections={connections}
            motorControls={effectiveMotorControls}
            simulationObstacles={simulationObstacles}
            simulationMode={mode === 'simulation'}
            selectedInstanceId={selectedInstanceId}
            selectedInstanceIds={simulationSelectedInstanceIds}
            assemblyGroupInstanceIds={activeRobotGroup?.instanceIds ?? []}
            isSimulating={isSimulating}
            autoSnap={autoSnap}
            preventOverlap={preventOverlap}
            solidFloor={mode === 'simulation' ? true : solidFloor}
            environment={environment}
            transformMode={transformMode}
            cameraAction={cameraAction}
            onSelect={selectInstance}
            onAreaSelect={selectInstances}
            onTransformStart={beginSelectedTransform}
            onTransformEnd={(position: PlacedPart['position'], rotation: PlacedPart['rotation']) => {
              setSelectedTransform(position, rotation)
            }}
            onConnectedGroupMove={moveConnectedGroup}
            onConnectedGroupRotate={rotateConnectedGroup}
            onSimulationObstacleMove={moveSimulationObstacle}
            onSimulationImpact={applySimulationImpact}
          />
        </Suspense>
      </SceneErrorBoundary>

      {(mode === 'assembly' || (mode === 'simulation' && !isSimulating)) && selectedInstanceId && (
        <div className={`axis-guide ${transformMode}`} aria-label={`${transformMode === 'move' ? '이동' : '회전'} 방향 안내`}>
          <strong>{transformMode === 'move' ? '이동' : '회전'}</strong>
          {transformMode === 'move' ? (
            <>
              <span className="axis-x"><i>X축</i><kbd>A/D</kbd></span>
              <span className="axis-z"><i>Z축</i><kbd>W/S</kbd></span>
              <span className="axis-y"><i>Y축</i><kbd>Q/E</kbd></span>
            </>
          ) : (
            <>
              <span className="axis-x"><i>X축</i><kbd>W/S</kbd></span>
              <span className="axis-y"><i>Y축</i><kbd>A/D</kbd></span>
              <span className="axis-z"><i>Z축</i><kbd>Q/E</kbd></span>
            </>
          )}
        </div>
      )}

      {mode === 'simulation' && (
        <div className={`simulation-floating-controls ${hasCodingProgram ? 'has-program' : 'manual-program'}`} aria-label="시뮬레이션 실행 제어">
          <button
            type="button"
            className="simulation-program-summary"
            aria-label={`${codingProgramName || '메인 프로그램'} 편집`}
            disabled={isSimulating}
            onClick={() => setMode('coding')}
          >
            <i aria-hidden="true">{'</>'}</i>
            <span>
              <strong>{codingProgramName || '메인 프로그램'}</strong>
              <small>{isSimulating && codingRunStatus === 'running'
                ? activeCodingBlock?.title ?? '프로그램 준비 중'
                : codingRunStatus === 'complete'
                  ? '코드 완료 · 물리 시뮬레이션 계속 중'
                  : codingBlockedReason
                    ? codingBlockedReason
                    : hasCodingProgram
                      ? `명령 ${codingCommands}개 · 약 ${Math.max(0.1, codingProgramEstimatedSeconds(codingProgram)).toFixed(1)}초`
                      : '코딩 탭에서 명령을 추가하세요'}</small>
            </span>
            <em>{isSimulating
              ? codingRunStatus === 'running' ? '코딩 중' : '물리 실행 중'
              : '편집'}</em>
          </button>
          <button
            type="button"
            className="simulation-run-button"
            aria-pressed={isSimulating}
            disabled={!placedParts.length || Boolean(codingBlockedReason)}
            onClick={toggleSimulationFromProgramBar}
          >
            <Icon name={isSimulating ? 'stop' : 'play'} size={17} />
            {isSimulating
              ? codingRunStatus === 'running' ? '코딩 실행 중지' : '시뮬레이션 중지'
              : hasCodingProgram ? '코딩 실행' : '시뮬레이션 실행'}
          </button>
        </div>
      )}

      <details
        className="camera-tools"
        aria-label="카메라 도구"
        open={cameraToolsOpen}
        onToggle={(event) => setCameraToolsOpen(event.currentTarget.open)}
      >
        <summary>카메라</summary>
        <div>
          <button onClick={() => { runCameraAction('focus'); setCameraToolsOpen(false) }} disabled={!selectedInstanceId}>선택 맞춤 F</button>
          <button className="default-camera" onClick={() => { runCameraAction('all'); setCameraToolsOpen(false) }}>전체 보기</button>
          <button title="한 번만 상단 시점으로 이동" onClick={() => { runCameraAction('top'); setCameraToolsOpen(false) }}>상단</button>
          <button title="한 번만 정면 시점으로 이동" onClick={() => { runCameraAction('front'); setCameraToolsOpen(false) }}>정면</button>
          <button title="한 번만 기본 원근 시점으로 이동" onClick={() => { runCameraAction('reset'); setCameraToolsOpen(false) }}>기본 시점 0</button>
        </div>
      </details>
    </section>
  )
}
